import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { clone, isNil } from 'lodash-es';
import { isValidElement } from 'react';
import { InterimLazyIcon, InterimNormalIcon, InterimStaleIcon } from '../Icons';
import { theme } from '../theme';
import { StyledMark } from './styled';
import { AUTO_POSITION, GRID_SIZE, NODE_DIMENSION, REACT_FLOW_CLASS, TILE_NODE_HEIGHT, TILE_NODE_WIDTH } from './tokens';
export function isElementInReactFlow(element) {
    return !!element.closest(`.${REACT_FLOW_CLASS}`);
}
export function isGraphPresent() {
    return Boolean(document.querySelector(`.${REACT_FLOW_CLASS}`));
}
export function getTileSize(noOfSourceHandles = 0, noOfTargetHandles = 0) {
    return {
        height: TILE_NODE_HEIGHT,
        width: TILE_NODE_WIDTH
    };
}
export function getSize(noOfSourceHandles = 0, noOfTargetHandles = 0, size) {
    const maxPorts = Math.max(noOfSourceHandles, noOfTargetHandles);
    let divider = 3.5;
    if (maxPorts >= 4) {
        size = size * maxPorts / divider;
    }
    return size;
}
export function getNodeSize(noOfSourceHandles = 0, noOfTargetHandles = 0) {
    const size = getSize(noOfSourceHandles, noOfTargetHandles, NODE_DIMENSION);
    return {
        height: size,
        width: size
    };
}
export function stripNodeProperties(node) {
    const { data, ...restNode } = node;
    // strip all react elements and functions
    const cloneableData = Object.fromEntries(Object.entries(data).filter(([key, value])=>{
        return !/*#__PURE__*/ (isValidElement(value) || typeof value === 'function');
    }));
    const _node = clone(restNode);
    const _data = clone(cloneableData);
    _node.data = _data;
    return _node;
}
export function defaultFilterNodes(node) {
    return true;
}
export function snapPosition(position) {
    return {
        x: position.x ? GRID_SIZE * Math.round(position.x / GRID_SIZE) : position.x,
        y: position.y ? GRID_SIZE * Math.round(position.y / GRID_SIZE) : position.y
    };
}
export function checkRectangleOverlap(rect1, rect2) {
    const left1 = rect1.x;
    const right1 = rect1.x + rect1.width;
    const top1 = rect1.y;
    const bottom1 = rect1.y + rect1.height;
    const left2 = rect2.x;
    const right2 = rect2.x + rect2.width;
    const top2 = rect2.y;
    const bottom2 = rect2.y + rect2.height;
    return left1 < right2 && right1 > left2 && top1 < bottom2 && bottom1 > top2;
}
export function isUnPositioned(position) {
    return position.x > AUTO_POSITION || position.y > AUTO_POSITION || isNil(position.x) || isNil(position.y);
}
export function isNodeNotPositioned(node) {
    return isUnPositioned(node.position);
}
/**
 * Note the getRectOfNodes util from react-flow, doesn't give rect based on absolute position,
 * until its explicitly set.
 */ export function getNodeRect(node, nodeInternals) {
    const nodeInternal = nodeInternals.get(node.id);
    const position = nodeInternals.get(node.id)?.positionAbsolute || node.position;
    const width = nodeInternal?.width || node.width || NODE_DIMENSION;
    const height = nodeInternal?.height || node.height || NODE_DIMENSION;
    return {
        ...position,
        width,
        height
    };
}
export function getTextWithHighlight(text = '', searchQuery = '', style) {
    if (!searchQuery || searchQuery.trim().length === 0) return text;
    const matchIndex = text.toLowerCase().indexOf(searchQuery.toLowerCase());
    if (matchIndex !== -1) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                text.substring(0, matchIndex),
                /*#__PURE__*/ _jsx(StyledMark, {
                    style: style,
                    children: text.substring(matchIndex, matchIndex + searchQuery.length)
                }),
                text.substring(matchIndex + searchQuery.length)
            ]
        });
    } else {
        return text;
    }
}
export function getInterimIcon({ isStale, isLazyLoadInterim }) {
    const height = theme.sizes.x24;
    const width = theme.sizes.x24;
    if (isLazyLoadInterim) return /*#__PURE__*/ _jsx(InterimLazyIcon, {
        height: height,
        width: width
    });
    if (isStale) return /*#__PURE__*/ _jsx(InterimStaleIcon, {
        height: height,
        width: width
    });
    return /*#__PURE__*/ _jsx(InterimNormalIcon, {
        height: height,
        width: width
    });
}
export const sortNodesByTopLeft = (nodes)=>{
    return nodes.sort((nodeA, nodeB)=>{
        if (nodeA.position.y !== nodeB.position.y) {
            return nodeA.position.y - nodeB.position.y;
        }
        return nodeA.position.x - nodeB.position.x;
    });
};
/**
 * Get the absolute position of a React Flow node
 * Falls back to regular position if positionAbsolute is not available
 *
 * @param node - React Flow Node instance
 * @returns Absolute position { x, y }
 */ export function getNodeAbsolutePosition(node) {
    return node.positionAbsolute ? {
        ...node.positionAbsolute
    } : {
        ...node.position
    };
}
